(function () {
  'use strict';
  const one = (s, root = document) => root.querySelector(s);
  const all = (s, root = document) => Array.from(root.querySelectorAll(s));
  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const visible = node => !node.closest('[hidden], [inert]') && node.getClientRects().length > 0;
  const focusable = root => all('a[href], button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), summary, [tabindex="0"]', root).filter(visible);
  let modal = null;
  let returnFocus = null;
  let inerted = [];
  let cartQueue = Promise.resolve();
  let cartBusy = 0;
  let draftNote = null;
  const variantRequests = new WeakMap();
  const bound = new WeakSet();
  const disclosures = new WeakSet();
  const compactLayout = window.matchMedia('(max-width: 1100px)');
  const quantityTimers = new WeakMap();
  const quantityCommitted = new WeakMap();
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('is-in'); revealObserver.unobserve(entry.target); }
    });
  }, { threshold: .04 }) : null;

  function errorAt(node, message) {
    if (!node) return;
    node.textContent = message || '';
    node.hidden = !message;
  }

  async function jsonRequest(url, options = {}) {
    const response = await fetch(url, { ...options, headers: { Accept: 'application/json', ...options.headers } });
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : '');
    return data;
  }

  function closeModal(restore = true) {
    if (!modal) return;
    modal.dataset.open = 'false';
    modal.setAttribute('aria-hidden', 'true');
    modal.inert = true;
    const toggle = one('[data-menu-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    inerted.forEach(node => { node.inert = false; });
    inerted = [];
    document.documentElement.classList.remove('cart-open', 'menu-open');
    modal = null;
    if (restore && returnFocus?.isConnected) returnFocus.focus();
  }

  function openModal(panel, trigger) {
    closeModal(false);
    modal = panel;
    returnFocus = trigger || document.activeElement;
    panel.inert = false;
    panel.dataset.open = 'true';
    panel.setAttribute('aria-hidden', 'false');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    document.documentElement.classList.add(panel.matches('[data-cart]') ? 'cart-open' : 'menu-open');
    let branch = panel;
    while (branch.parentElement && branch.parentElement !== document.documentElement) {
      Array.from(branch.parentElement.children).forEach(sibling => {
        if (sibling !== branch && !sibling.inert && !sibling.matches('script, style, link, [data-cart-scrim]')) {
          sibling.inert = true;
          inerted.push(sibling);
        }
      });
      branch = branch.parentElement;
    }
    (one('[data-cart-close], [data-menu-close]', panel) || focusable(panel)[0] || panel).focus();
  }

  function renderDrawer(html) {
    if (!html) throw new Error('');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = one('[data-cart]', doc);
    const current = one('[data-cart]');
    if (!next || !current) throw new Error('');
    const focused = current.contains(document.activeElement) ? document.activeElement : null;
    const focusKey = focused?.dataset.key;
    const focusId = focused?.id;
    ['[data-cart-lines]', '[data-cart-empty]', '[data-cart-foot]'].forEach(selector => {
      const replacement = one(selector, next);
      const previous = one(selector, current);
      if (replacement && previous) previous.replaceWith(replacement);
    });
    const count = next.dataset.itemCount;
    current.dataset.itemCount = count;
    all('[data-cart-badge]').forEach(node => { node.textContent = count; node.dataset.count = count; });
    const label = one('[data-cart-count]', current);
    if (label) label.textContent = '(' + count + ')';
    const note = one('[data-cart-note]', current);
    if (note && draftNote !== null) note.value = draftNote;
    setExpressBusy(current, cartBusy > 0 || drawerHasChanges(current));
    if (modal === current && focused && !focused.isConnected) {
      const replacement = focusKey ? all('[data-key]', current).find(node => node.dataset.key === focusKey) : focusId ? document.getElementById(focusId) : null;
      (replacement || one('[data-cart-close]', current)).focus();
    }
  }

  async function refreshDrawer() {
    if (!one('[data-cart]')) return;
    const data = await jsonRequest(rootUrl() + '?sections=cart-drawer');
    renderDrawer(data['cart-drawer']);
  }

  function setExpressBusy(root, busy) {
    all('[data-cart-accelerated]', root).forEach(node => { node.inert = busy; node.setAttribute('aria-busy', String(busy)); });
  }

  function drawerHasChanges(panel) {
    return all('[data-cart-quantity], [data-cart-note]', panel).some(field => field.value !== field.defaultValue);
  }

  function syncPaymentTerms(product, value) {
    all('[data-installment-variant]', product).forEach(field => { field.value = value; field.setAttribute('value', value); field.dispatchEvent(new Event('change', {bubbles:true})); });
  }

  function queueCart(task) {
    cartBusy += 1;
    const panel = one('[data-cart]');
    panel?.setAttribute('aria-busy', 'true');
    if (panel) setExpressBusy(panel, true);
    const result = cartQueue.then(task);
    cartQueue = result.catch(() => {}).finally(() => {
      cartBusy -= 1;
      if (!cartBusy) { const current = one('[data-cart]'); current?.removeAttribute('aria-busy'); if (current) setExpressBusy(current, drawerHasChanges(current)); }
    });
    return result;
  }

  async function updateCart(key, quantity) {
    const error = one('[data-cart-error]');
    errorAt(error, '');
    try {
      await queueCart(async () => {
        const data = await jsonRequest(rootUrl() + 'cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'], sections_url: rootUrl() })
        });
        if (data.sections?.['cart-drawer']) renderDrawer(data.sections['cart-drawer']);
        else await refreshDrawer();
      });
      return true;
    } catch (failure) {
      try { await refreshDrawer(); } catch (_) {}
      const currentError = one('[data-cart-error]');
      errorAt(currentError, failure.message || currentError?.dataset.errorMessage);
      return false;
    }
  }

  async function addToCart(form) {
    if (form.dataset.submitting === 'true') return;
    form.dataset.submitting = 'true';
    const button = one('[data-add-to-cart]', form);
    const error = one('[data-product-error]', form);
    errorAt(error, '');
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    const data = new FormData(form);
    data.append('sections', 'cart-drawer');
    data.append('sections_url', rootUrl());
    let added = false;
    try {
      await queueCart(async () => {
        const result = await jsonRequest(rootUrl() + 'cart/add.js', { method: 'POST', body: data });
        added = true;
        if (result.sections?.['cart-drawer']) renderDrawer(result.sections['cart-drawer']);
        else await refreshDrawer();
      });
      const panel = one('[data-cart]');
      if (panel) openModal(panel, button);
    } catch (failure) {
      errorAt(error, added ? error?.dataset.addedMessage : failure.message || error?.dataset.errorMessage);
    } finally {
      delete form.dataset.submitting;
      if (button) {
        button.removeAttribute('aria-busy');
        button.disabled = form.closest('[data-product-root]')?.dataset.available === 'false';
      }
    }
  }

  async function chooseVariant(button) {
    const product = button.closest('[data-product-root]');
    const field = one('[data-variant-field]', product);
    if (!field) { window.location.assign(button.href); return; }
    variantRequests.get(product)?.abort();
    const controller = new AbortController();
    variantRequests.set(product, controller);
    const optionSelection = button.matches('[data-option-value-id]');
    if (optionSelection && button.dataset.productUrl !== product.dataset.productUrl) { window.location.assign(button.href); return; }
    if (!optionSelection) all('[data-variant]', product).forEach(node => node.setAttribute('aria-pressed', String(node === button)));
    const submit = one('[data-add-to-cart]', product);
    const error = one('[data-product-error]', product);
    errorAt(error, '');
    if (!optionSelection) { field.value = button.dataset.variantId; field.setAttribute('value', field.value); }
    field.dispatchEvent(new Event('change', { bubbles: true }));
    syncPaymentTerms(product, field.value);
    if (!optionSelection) product.dataset.available = button.dataset.available;
    if (submit) submit.disabled = true;
    product.setAttribute('aria-busy', 'true');
    const url = new URL(window.location.href);
    if (optionSelection) {
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', new URL(button.href).searchParams.get('option_values'));
    } else { url.searchParams.delete('option_values'); url.searchParams.set('variant', field.value); }
    url.searchParams.set('section_id', product.dataset.sectionId);
    try {
      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) throw new Error('');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (controller.signal.aborted || !product.isConnected) return;
      const next = one('[data-product-root]', doc);
      if (!next) throw new Error('');
      ['[data-product-price]', '[data-sku]', '[data-product-inventory]', '[data-product-tax]'].forEach(selector => {
        const replacement = one(selector, next), current = one(selector, product);
        if (replacement && current) current.innerHTML = replacement.innerHTML;
      });
      if (optionSelection) {
        field.value = next.dataset.selectedVariant || '';
        field.setAttribute('value', field.value);
        field.dispatchEvent(new Event('change', { bubbles: true }));
        syncPaymentTerms(product, field.value);
        product.dataset.available = next.dataset.available;
        const picker = one('[data-option-picker]', product), nextPicker = one('[data-option-picker]', next);
        if (picker && nextPicker) {
          const chosenId = button.dataset.optionValueId;
          picker.innerHTML = nextPicker.innerHTML;
          all('[data-option-value-id]', picker).find(node => node.dataset.optionValueId === chosenId)?.focus();
        }
        if (field.value) { url.searchParams.delete('option_values'); url.searchParams.set('variant', field.value); }
      }
      const nextSubmit = one('[data-add-to-cart]', next);
      if (submit && nextSubmit) { submit.innerHTML = nextSubmit.innerHTML; submit.disabled = nextSubmit.disabled; }
      const qty = one('[data-product-quantity]', product), nextQty = one('[data-product-quantity]', next);
      if (qty && nextQty) {
        ['min', 'max', 'step'].forEach(name => {
          if (nextQty.hasAttribute(name)) qty.setAttribute(name, nextQty.getAttribute(name)); else qty.removeAttribute(name);
        });
        if (!qty.checkValidity()) qty.value = nextQty.value;
      }
      if (button.dataset.mediaId) {
        const thumb = all('[data-media-switch]', product).find(node => node.dataset.mediaTarget.endsWith('-' + button.dataset.mediaId));
        if (thumb) switchMedia(thumb);
      }
      url.searchParams.delete('section_id');
      window.history.replaceState({}, '', url);
    } catch (failure) {
      if (failure.name !== 'AbortError') {
        errorAt(error, error?.dataset.errorMessage);
        window.location.assign(button.href);
      }
    } finally {
      if (variantRequests.get(product) === controller) product.removeAttribute('aria-busy');
    }
  }

  function pauseMedia(root) {
    all('audio, video', root).forEach(media => media.pause());
    all('iframe', root).forEach(frame => {
      if (!frame.src) return;
      const url = new URL(frame.src, location.href);
      if (/youtube(?:-nocookie)?\.com$/.test(url.hostname)) frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), url.origin);
      if (url.hostname === 'player.vimeo.com') frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), url.origin);
    });
  }

  function switchMedia(button) {
    const product = button.closest('[data-product-root]');
    const target = document.getElementById(button.dataset.mediaTarget);
    if (!target || !product) return;
    all('[data-media-item]', product).forEach(item => { if (item !== target) pauseMedia(item); item.hidden = item !== target; });
    all('[data-media-switch]', product).forEach(thumb => {
      thumb.classList.toggle('is-active', thumb === button);
      thumb.setAttribute('aria-pressed', String(thumb === button));
    });
  }

  function selectTab(button, focus = false) {
    const group = button.closest('[data-tabs]');
    all('[data-tab-btn]', group).forEach(tab => { tab.setAttribute('aria-selected', String(tab === button)); tab.tabIndex = tab === button ? 0 : -1; });
    all('[data-tab-panel]', group).forEach(panel => {
      panel.hidden = panel.dataset.tabPanel !== button.dataset.tabBtn;
      if (!panel.hidden) { panel.classList.add('is-in'); if (panel.matches('[data-rail]')) updateRail(panel); }
    });
    if (focus) button.focus();
  }

  function playTrack(button) {
    const list = button.closest('[data-tracklist]');
    const audio = one('[data-track-audio]', list);
    const status = one('[data-track-status]', list);
    if (!audio) return;
    if (audio.getAttribute('src') === button.dataset.file && !audio.paused) {
      audio.pause(); return;
    }
    all('audio[data-track-audio]').forEach(other => { if (other !== audio) other.pause(); });
    if (audio.getAttribute('src') !== button.dataset.file || audio.error) { audio.src = button.dataset.file; audio.load(); }
    all('[data-track]', list).forEach(node => node.setAttribute('aria-pressed', String(node === button)));
    audio.play().then(() => {
      if (status) status.textContent = list.dataset.playing + ' ' + one('.track__name', button).textContent;
    }).catch(() => {
      button.setAttribute('aria-pressed', 'false');
      if (status) status.textContent = list.dataset.failed;
    });
  }

  function initialize(root = document) {
    all('[data-responsive-disclosure]', root).forEach(details => {
      if (disclosures.has(details)) return;
      disclosures.add(details);
      if (!details.hasAttribute('data-disclosure-ready')) {
        details.open = !compactLayout.matches;
        details.setAttribute('data-disclosure-ready', '');
      }
      details.addEventListener('toggle', () => {
        if (!details.open) pauseMedia(details);
      });
    });
    all('[data-product-root]', root).forEach(product => {
      const field = one('[data-variant-field]', product);
      if (field && !field.value && product.dataset.selectedVariant) { field.value = product.dataset.selectedVariant; field.setAttribute('value', field.value); }
    });
    all('[data-reveal]', root).forEach(node => {
      if (!revealObserver || reduced() || document.documentElement.dataset.animations === 'false' || window.Shopify?.designMode) node.classList.add('is-in');
      else revealObserver.observe(node);
    });
    all('[data-cart], [data-menu]', root).forEach(panel => { if (panel !== modal) { panel.inert = true; panel.setAttribute('aria-hidden', 'true'); } });
    all('[data-rail]', root).forEach(rail => {
      if (bound.has(rail)) return;
      bound.add(rail);
      rail.addEventListener('scroll', () => updateRail(rail), { passive: true });
      updateRail(rail);
    });
    all('[data-tracklist]', root).forEach(list => {
      if (bound.has(list)) return;
      bound.add(list);
      const audio = one('[data-track-audio]', list);
      ['pause', 'ended', 'error'].forEach(type => audio?.addEventListener(type, () => {
        all('[data-track]', list).forEach(button => button.setAttribute('aria-pressed', 'false'));
        const status = one('[data-track-status]', list);
        if (status) status.textContent = audio.error || type === 'error' ? list.dataset.failed : type === 'ended' ? list.dataset.finished : list.dataset.paused;
      }));
    });
    all('[data-recommendations]', root).forEach(grid => {
      if (bound.has(grid)) return;
      bound.add(grid);
      fetch(grid.dataset.url).then(response => { if (!response.ok) throw new Error(''); return response.text(); }).then(html => {
        if (!grid.isConnected) return;
        const next = one('[data-recommendations]', new DOMParser().parseFromString(html, 'text/html'));
        if (next?.children.length) { grid.innerHTML = next.innerHTML; grid.classList.add('is-in'); }
        else grid.closest('section').hidden = !window.Shopify?.designMode;
      }).catch(() => { if (grid.isConnected) grid.closest('section').hidden = !window.Shopify?.designMode; });
    });
    all('[data-address-country]', root).forEach(country => {
      if (bound.has(country)) return;
      bound.add(country);
      const selected = all('option', country).find(option => option.value === country.dataset.default || option.textContent === country.dataset.default);
      if (selected) country.value = selected.value;
      updateProvinces(country, true);
    });
    all('details:has(.form__error)', root).forEach(details => { details.open = true; });
    all('[data-gift-qr]', root).forEach(node => {
      if (bound.has(node) || !window.QRCode || !node.dataset.identifier) return;
      bound.add(node);
      new window.QRCode(node, { text: node.dataset.identifier, width: 160, height: 160 });
      one('img', node)?.setAttribute('alt', node.dataset.label);
    });
  }

  function updateRail(rail) {
    if (!visible(rail)) return;
    const end = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
    rail.classList.toggle('is-end', end);
    const section = rail.closest('section');
    if (!section) return;
    all('[data-rail-prev]', section).forEach(button => { button.disabled = rail.scrollLeft <= 2; });
    all('[data-rail-next]', section).forEach(button => { button.disabled = end; });
  }

  compactLayout.addEventListener?.('change', () => {
    all('[data-responsive-disclosure]').forEach(details => { details.open = !compactLayout.matches; });
  });

  function updateProvinces(country, initial = false) {
    const form = country.closest('form');
    const province = one('[data-address-province]', form);
    if (!province) return;
    const values = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]');
    province.replaceChildren(...values.map(([value, label]) => new Option(label, value)));
    if (initial) province.value = province.dataset.default || '';
    province.closest('[data-province-field]').hidden = !values.length;
    province.disabled = !values.length;
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('button, a, [data-cart-scrim]');
    if (!button) return;
    if (button.matches('[data-cart-close], [data-menu-close], [data-cart-scrim]')) { closeModal(); return; }
    if (button.matches('[data-menu-toggle]')) {
      const panel = one('[data-menu]');
      if (!panel) return;
      if (modal === panel) closeModal(); else { openModal(panel, button); button.setAttribute('aria-expanded', 'true'); }
    }
    if (button.matches('[data-cart-open]') && one('[data-cart]')) {
      event.preventDefault(); openModal(one('[data-cart]'), button);
      refreshDrawer().catch(() => errorAt(one('[data-cart-error]'), one('[data-cart-error]')?.dataset.errorMessage));
    }
    if (button.matches('[data-cart-remove]')) { event.preventDefault(); updateCart(button.dataset.key, 0); }
    if (button.matches('[data-variant], [data-option-value-id]') && !event.ctrlKey && !event.metaKey && !event.shiftKey) { event.preventDefault(); chooseVariant(button); }
    if (button.matches('[data-media-switch]')) switchMedia(button);
    if (button.matches('[data-track]')) playTrack(button);
    if (button.matches('[data-print]')) window.print();
    if (button.matches('[data-gift-copy]')) {
      const code = one('[data-gift-code]');
      if (code && navigator.clipboard) navigator.clipboard.writeText(code.textContent.replace(/\s/g, '')).then(() => {
        one('[data-gift-status]').textContent = button.dataset.copied;
      }).catch(() => { const selection = window.getSelection(); const range = document.createRange(); range.selectNodeContents(code); selection.removeAllRanges(); selection.addRange(range); });
    }
    if (button.matches('[data-tab-btn]')) selectTab(button);
    if (button.matches('[data-rail-prev], [data-rail-next]')) {
      const section = button.closest('section');
      const rail = all('[data-rail]', section).find(visible);
      if (rail) rail.scrollBy({ left: (button.matches('[data-rail-prev]') ? -1 : 1) * rail.clientWidth * .8, behavior: reduced() ? 'auto' : 'smooth' });
    }
    if (button.closest('[data-menu]') && button.matches('a')) closeModal(false);
  });

  document.addEventListener('click', event => all('[data-nav-disclosure][open]').forEach(details => { if (!details.contains(event.target)) details.open = false; }));
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      if (modal) { closeModal(); return; }
      const disclosure = event.target.closest('[data-nav-disclosure][open]');
      if (disclosure) { disclosure.open = false; one('summary', disclosure).focus(); }
    }
    if (event.key === 'Tab' && modal) {
      const nodes = focusable(modal), first = nodes[0], last = nodes[nodes.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || !modal.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !modal.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    }
    const tab = event.target.closest('[data-tab-btn]');
    if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      event.preventDefault(); const tabs = all('[data-tab-btn]', tab.closest('[data-tabs]'));
      const direction = document.dir === 'rtl' ? -1 : 1;
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (tabs.indexOf(tab) + (event.key === 'ArrowRight' ? direction : -direction) + tabs.length) % tabs.length;
      selectTab(tabs[index], true);
    }
    if (event.key === ' ' && event.target.matches('[data-variant], [data-option-value-id]')) { event.preventDefault(); chooseVariant(event.target); }
  });

  function commitQuantity(field) {
    clearTimeout(quantityTimers.get(field));
    if (!field.isConnected || !field.checkValidity() || field.value === '' || quantityCommitted.get(field) === field.value) return;
    quantityCommitted.set(field, field.value);
    updateCart(field.dataset.key, Number(field.value)).then(success => {
      if (!success) quantityCommitted.delete(field);
    });
  }
  document.addEventListener('input', event => {
    const field = event.target;
    if (field.matches('[data-cart-note]')) draftNote = field.value;
    if (field.matches('[data-cart-note], [data-cart-quantity]')) { const panel = one('[data-cart]'); if (panel) setExpressBusy(panel, cartBusy > 0 || drawerHasChanges(panel)); }
    if (field.closest('.cart-form')) { setExpressBusy(field.form, true); const notice = one('[data-cart-changed]', field.form); if (notice) notice.hidden = false; }
    if (field.matches('[data-cart-quantity]')) {
      clearTimeout(quantityTimers.get(field));
      quantityTimers.set(field, setTimeout(() => commitQuantity(field), 450));
    }
  });
  document.addEventListener('change', event => {
    const field = event.target;
    if (field.matches('[data-cart-quantity]')) commitQuantity(field);
    if (field.matches('[data-address-country]')) updateProvinces(field);
    if (field.matches('[data-cart-note]')) {
      const note = field.value;
      const status = one('[data-cart-note-status]', field.parentNode);
      queueCart(() => jsonRequest(rootUrl() + 'cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note })
      })).then(() => {
        const panel = one('[data-cart]'), currentNote = panel && one('[data-cart-note]', panel);
        if (currentNote) currentNote.defaultValue = note;
        if (panel) setExpressBusy(panel, cartBusy > 0 || drawerHasChanges(panel));
        if (status) status.textContent = field.dataset.savedMessage;
      }).catch(() => { if (status) status.textContent = field.dataset.errorMessage; });
    }
  });

  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches('[data-address-delete]') && !window.confirm(form.dataset.confirm)) { event.preventDefault(); return; }
    if (form.matches('form.product-form') && one('[data-cart]')) { event.preventDefault(); addToCart(form); }
    if (form.matches('[data-drawer-checkout]')) {
      const note = one('[data-cart-note]');
      if (note) one('[data-checkout-note]', form).value = note.value;
      if (cartBusy) { event.preventDefault(); cartQueue.then(() => { if (one('[data-cart-error]')?.hidden === false) return; if (form.isConnected) form.requestSubmit(one('[name="checkout"]', form)); else one('[data-drawer-checkout]')?.requestSubmit(one('[data-drawer-checkout] [name="checkout"]')); }); }
    }
  });

  let ticking = false;
  function scrollChrome() {
    const y = window.scrollY, height = document.documentElement.scrollHeight - innerHeight;
    const line = one('[data-scroll-line]'), nav = one('[data-nav]');
    if (line) line.style.transform = 'scaleX(' + (height > 0 ? Math.min(y / height, 1) : 0) + ')';
    if (nav) { nav.dataset.scrolled = String(y > 8); nav.closest('.nav-shell').dataset.scrolled = String(y > 8); }
    ticking = false;
  }
  window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(scrollChrome); } }, { passive: true });
  window.addEventListener('resize', () => {
    if (innerWidth > 1300 && modal?.matches('[data-menu]')) closeModal();
    all('[data-rail]').forEach(updateRail);
  });
  window.addEventListener('pageshow', event => { if (event.persisted) refreshDrawer().catch(() => {}); });
  document.addEventListener('shopify:section:load', event => { if (modal && !modal.isConnected) closeModal(false); initialize(event.target); scrollChrome(); });
  document.addEventListener('shopify:section:unload', event => {
    if (modal && event.target.contains(modal)) closeModal(false);
    all('[data-reveal]', event.target).forEach(node => revealObserver?.unobserve(node));
    all('[data-product-root]', event.target).forEach(node => variantRequests.get(node)?.abort());
    pauseMedia(event.target);
  });
  document.addEventListener('shopify:section:reorder', () => initialize());
  document.addEventListener('shopify:block:select', event => {
    const details = event.target.closest('details');
    if (details) details.open = true;
    all('[data-reveal]', event.target).forEach(node => node.classList.add('is-in'));
  });
  function ready() {
    initialize(); scrollChrome();
    if (!reduced() && document.documentElement.dataset.animations !== 'false' && !window.Shopify?.designMode) document.documentElement.classList.add('motion-ready');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true }); else ready();
})();
